// strips trailing slashes and `/api` suffix to avoid double `/api/api/...` paths
function normalizeApiBase(raw: string): string {
  let s = (raw ?? '').trim().replace(/\/+$/, '')
  if (s.endsWith('/api')) s = s.slice(0, -4).replace(/\/+$/, '')
  return s
}

export const API_BASE_URL = normalizeApiBase(import.meta.env.VITE_API_URL ?? '')

const BASE_URL = API_BASE_URL

function getToken(): string | null {
  return localStorage.getItem('access_token')
}

function setTokens(access: string, refresh: string) {
  localStorage.setItem('access_token', access)
  localStorage.setItem('refresh_token', refresh)
  window.dispatchEvent(new CustomEvent('seacuredb:auth', { detail: { type: 'SIGNED_IN', token: access } }))
}

function clearTokens() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  window.dispatchEvent(new CustomEvent('seacuredb:auth', { detail: { type: 'SIGNED_OUT' } }))
}

function decodeJwt(token: string): Record<string, any> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}

let refreshPromise: Promise<boolean> | null = null

async function attemptRefresh(): Promise<boolean> {
  // deduplicate concurrent refresh attempts
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return false
    try {
      const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!res.ok) return false
      const data = await res.json()
      setTokens(data.access_token, data.refresh_token)
      return true
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

// shared API-down signals, used by both fetchWithAuthRetry and
// request() below. hoisted out of request() in commit 1b3ae58 so the
// multipart-upload path can throw the same friendly error class.
// closes the coverage gap that left parse-assessment / evidence
// upload / asset-inventory parse / chunked-upload all surfacing the
// "CORS mystery" UX on CF Free's tunnel-down 502 / 504
const RETRYABLE_DOWN_STATUSES = new Set([502, 503, 504])
const API_DOWN_MESSAGE        = 'API temporarily unavailable — please try again in a moment'

// thrown by both fetchWithAuthRetry and request() (via its error
// object) when the upstream can't be reached. the .isApiDown flag
// lets call sites tell this class of failure apart from "request was
// bad" without substring-matching the message text
export class ApiDownError extends Error {
  readonly isApiDown = true as const
  constructor(message: string = API_DOWN_MESSAGE) { super(message); this.name = 'ApiDownError' }
}

// multipart-aware fetch, reuses attemptRefresh()'s 401-retry behavior.
// unlike request() this does NOT stringify the body or set Content-Type,
// it's for FormData uploads where the browser sets the boundary.
// token re-read from localStorage on the first attempt and after
// a successful refresh, so we always send the latest Bearer header.
//
// 2026-06-11: API-down classification added to mirror request(). a
// TypeError from fetch() (CORS-blocked preflight, DNS, timeout, refused)
// OR a 502/503/504 from CF/nginx now throws ApiDownError so callers
// don't have to substring-match "Parse failed" / "Upload failed" /
// raw HTTP statuses. no auto-retry for POSTs, multipart bodies aren't
// replayable from a consumed stream and most callers here are POST
// uploads where the user re-triggers deliberately
export async function fetchWithAuthRetry(
  url: string,
  init: RequestInit = {}
): Promise<Response> {
  const buildInit = (): RequestInit => {
    const headers = new Headers(init.headers ?? {})
    const token = getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    else headers.delete('Authorization')
    return { ...init, headers }
  }

  let first: Response
  try {
    first = await fetch(url, buildInit())
  } catch (err) {
    // TypeError = no response object at all. throw the friendly error
    // class. any other unexpected throw (rare, usually AbortError)
    // propagates as-is
    if (err instanceof TypeError) throw new ApiDownError()
    throw err
  }
  if (RETRYABLE_DOWN_STATUSES.has(first.status)) throw new ApiDownError()

  if (first.status !== 401) return first

  // attempt one refresh + one retry
  const had_token = !!getToken()
  if (!had_token) return first  // no point refreshing

  // multipart bodies (FormData) can't be replayed. once fetch consumes
  // the stream on the first attempt, the retry sends an empty body and the
  // server returns 401 again because no file arrived. surface the original
  // 401 honestly so the caller can show "session expired, please log in"
  // rather than us silently corrupting a large upload
  const isMultipart = init.body instanceof FormData
  if (isMultipart) return first

  const refreshed = await attemptRefresh()
  if (!refreshed) return first  // surface original 401

  let retried: Response
  try {
    retried = await fetch(url, buildInit())
  } catch (err) {
    if (err instanceof TypeError) throw new ApiDownError()
    throw err
  }
  if (RETRYABLE_DOWN_STATUSES.has(retried.status)) throw new ApiDownError()
  return retried
}

// endpoints that don't accept a Bearer token and must not trigger the
// 401-refresh-retry path. hitting any of these through request() with a
// stale access token in localStorage would misread a genuine
// credentials-error 401 as an expired session and surface "Session expired"
// instead of the server's real error body
const UNAUTHENTICATED_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/signup',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
])

// API-down handling for the JSON/auto-refresh path. constants live at
// the top of the file (shared with fetchWithAuthRetry). request() does
// one extra trick over fetchWithAuthRetry: idempotent GETs auto-retry
// once after a 2s pause before surfacing the friendly message, since
// CF's cached 502 has usually moved past by then. POSTs / mutations
// get the message immediately so the user re-submits deliberately
const RETRY_DELAY_MS = 2000

// authenticated fetch with 401 refresh + retry
export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  _retrying = false
): Promise<{ data: T | null; error: { message: string; status?: number; body?: unknown } | null }> {
  const headers: Record<string, string> = {}
  const isUnauth = UNAUTHENTICATED_PATHS.has(path)
  const token = isUnauth ? null : getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response | null = null
  let networkError: unknown = null
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch (err) {
    networkError = err
  }

  // API-down classification: no response at all (TypeError) OR a
  // gateway-error status (502/503/504). both mean "upstream not
  // serving" rather than "request was bad". idempotent GETs
  // retry once after a 2s pause. mutations surface immediately
  // so the user decides whether to re-submit
  const isApiDown = networkError instanceof TypeError ||
                    (res != null && RETRYABLE_DOWN_STATUSES.has(res.status))
  if (isApiDown) {
    if (!_retrying && method === 'GET') {
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS))
      return request<T>(method, path, body, true)
    }
    return { data: null, error: { message: API_DOWN_MESSAGE } }
  }

  // any other thrown error from fetch (very rare, generally only
  // when AbortController triggers) goes through the
  // existing "Network error" path
  if (networkError) {
    return { data: null, error: { message: (networkError as Error).message ?? 'Network error' } }
  }
  // res is non-null here, the two networkError paths above cover the
  // null case
  const response = res as Response

  if (response.status === 401 && !_retrying && !isUnauth) {
    // only refresh/redirect if we actually sent a token
    if (token) {
      const refreshed = await attemptRefresh()
      if (refreshed) return request<T>(method, path, body, true)
      clearTokens()
      // avoid redirect loop if already on an auth page
      if (!window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/forgot') &&
          !window.location.pathname.startsWith('/reset')) {
        window.location.href = '/login'
      }
    }
    return { data: null, error: { message: 'Session expired' } }
  }

  const json = await response.json().catch(() => ({}))
  if (!response.ok) {
    // keep the parsed body on the error — routes like smart-search return
    // structured context (stage, sql, explanation) alongside `error`, and
    // dropping it here leaves callers with just a message string
    return { data: null, error: { message: json.error ?? `HTTP ${response.status}`, status: response.status, body: json } }
  }
  return { data: json as T, error: null }
}


type FilterOp = 'eq' | 'neq' | 'ilike' | 'like' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'is' | `not.${string}`

interface Filter {
  col: string
  op: FilterOp
  val: any
}

interface QueryResult<T> {
  data: T[] | null
  count?: number | null
  error: any
}

interface MutationResult<T> {
  data: T | T[] | null
  error: any
}

class InsertBuilder<T> implements PromiseLike<MutationResult<T>> {
  constructor(private readonly execute: () => Promise<MutationResult<T>>) {}

  select(_cols?: string) { return this }

  async single(): Promise<{ data: T | null; error: any }> {
    const { data, error } = await this.execute()
    return { data: Array.isArray(data) ? (data[0] ?? null) : data, error }
  }

  maybeSingle() { return this.single() }

  then<TResult1 = MutationResult<T>, TResult2 = never>(
    onfulfilled?: ((value: MutationResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected)
  }
}

class QueryBuilder<T = any> {
  private _table: string
  private _filters: Filter[] = []
  private _selectCols = '*'
  private _countExact = false
  private _orFilter: string | null = null
  private _orderCol: string | null = null
  private _orderAsc = true
  private _limitN: number | null = null
  private _offsetN: number | null = null
  private _pendingUpdate: any | null = null
  private _pendingDelete = false

  constructor(table: string) { this._table = table }

  select(cols = '*', opts?: { count?: 'exact' }) {
    this._selectCols = cols
    if (opts?.count === 'exact') this._countExact = true
    return this
  }
  eq(col: string, val: any) { this._filters.push({ col, op: 'eq', val }); return this }
  neq(col: string, val: any) { this._filters.push({ col, op: 'neq', val }); return this }
  ilike(col: string, val: any) { this._filters.push({ col, op: 'ilike', val }); return this }
  like(col: string, val: any) { this._filters.push({ col, op: 'like', val }); return this }
  gt(col: string, val: any) { this._filters.push({ col, op: 'gt', val }); return this }
  gte(col: string, val: any) { this._filters.push({ col, op: 'gte', val }); return this }
  lt(col: string, val: any) { this._filters.push({ col, op: 'lt', val }); return this }
  lte(col: string, val: any) { this._filters.push({ col, op: 'lte', val }); return this }
  in(col: string, vals: any[]) { this._filters.push({ col, op: 'in', val: vals.join(',') }); return this }
  is(col: string, val: any) { this._filters.push({ col, op: 'is', val }); return this }
  not(col: string, op: string, val: any) { this._filters.push({ col, op: `not.${op}`, val }); return this }
  or(filterString: string) { this._orFilter = filterString; return this }

  order(col: string, opts?: { ascending?: boolean }) {
    this._orderCol = col
    this._orderAsc = opts?.ascending ?? true
    return this
  }
  limit(n: number) { this._limitN = n; return this }
  offset(n: number) { this._offsetN = n; return this }
  range(from: number, to: number) { this._offsetN = from; this._limitN = to - from + 1; return this }
  async single(): Promise<{ data: T | null; count?: number | null; error: any }> {
    const result = await this._execute()
    if (!result.data?.length) {
      return { ...result, data: null, error: result.error ?? { message: 'No rows returned' } }
    }
    return { ...result, data: result.data[0] }
  }

  async maybeSingle(): Promise<{ data: T | null; count?: number | null; error: any }> {
    const result = await this._execute()
    return { ...result, data: result.data?.[0] ?? null }
  }

  private _buildParams(): string {
    const p = new URLSearchParams()
    if (this._selectCols !== '*') p.set('select', this._selectCols)
    for (const f of this._filters) {
      p.set(`${f.col}.${f.op}`, String(f.val))
    }
    if (this._orFilter) p.set('or', `(${this._orFilter})`)
    if (this._countExact) p.set('count', 'exact')
    if (this._orderCol) p.set('orderBy', `${this._orderCol}:${this._orderAsc ? 'asc' : 'desc'}`)
    if (this._limitN !== null) p.set('limit', String(this._limitN))
    if (this._offsetN !== null) p.set('offset', String(this._offsetN))
    return p.toString()
  }

  private _filterParams(): string {
    const p = new URLSearchParams()
    for (const f of this._filters) {
      p.set(`${f.col}.${f.op}`, String(f.val))
    }
    return p.toString()
  }

  // allow using as a Promise directly
  then<TResult1 = QueryResult<T>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this._execute().then(onfulfilled, onrejected)
  }

  private async _execute(): Promise<QueryResult<T>> {
    if (this._pendingUpdate !== null) {
      const params = this._filterParams()
      const { data, error } = await request<T[]>(
        'PUT',
        `/api/${this._table}${params ? `?${params}` : ''}`,
        this._pendingUpdate
      )
      const rows = data ?? []
      return { data: rows, error }
    }

    if (this._pendingDelete) {
      const params = this._filterParams()
      return request<T[]>('DELETE', `/api/${this._table}${params ? `?${params}` : ''}`)
    }

    const params = this._buildParams()
    const { data: rawData, error } = await request<any>('GET', `/api/${this._table}?${params}`)

    // count=exact: server returns { data: [...], count: N } envelope
    let rows: T[]
    let count: number | null = null
    if (this._countExact && rawData && typeof rawData === 'object' && 'data' in rawData) {
      rows = (rawData.data ?? []) as T[]
      count = rawData.count ?? null
    } else {
      rows = ((rawData ?? []) as T[])
    }

    return { data: rows, count, error }
  }

  insert(rowOrRows: any | any[]) {
    const doInsert = async () => {
      const { data, error } = await request<T | T[]>(
        'POST',
        `/api/${this._table}`,
        rowOrRows
      )
      return { data, error }
    }

    return new InsertBuilder<T>(doInsert)
  }

  update(updates: any) {
    const clone = new QueryBuilder<T>(this._table)
    clone._filters = [...this._filters]
    clone._selectCols = this._selectCols
    clone._pendingUpdate = updates
    return clone
  }

  delete() {
    const clone = new QueryBuilder<T>(this._table)
    clone._filters = [...this._filters]
    clone._pendingDelete = true
    return clone
  }

  // simplified upsert: delegates to insert, ignores conflict
  upsert(rowOrRows: any | any[], _opts?: { onConflict?: string }) {
    return this.insert(rowOrRows)
  }
}

export function from<T = any>(table: string): QueryBuilder<T> {
  return new QueryBuilder<T>(table)
}

export const auth = {
  signInWithPassword: async ({ email, password }: { email: string; password: string }) => {
    const { data, error } = await request<any>('POST', '/api/auth/login', { email, password })
    if (data && !error) {
      setTokens(data.access_token, data.refresh_token)
      const payload = decodeJwt(data.access_token)
      const user = { id: data.user.id, email: data.user.email, ...payload }
      const session = { access_token: data.access_token, user }
      return { data: { user, session }, error: null }
    }
    return { data: { user: null, session: null }, error }
  },

  signUp: async ({ email, password }: {
    email: string
    password: string
    options?: { emailRedirectTo?: string }
  }) => {
    const { data, error } = await request<any>('POST', '/api/auth/signup', { email, password })
    if (data && !error) {
      setTokens(data.access_token, data.refresh_token)
      const user = { id: data.user.id, email: data.user.email }
      return { data: { user, session: { access_token: data.access_token, user } }, error: null }
    }
    return { data: { user: null, session: null }, error }
  },

  acceptInvite: async ({ token, password }: { token: string; password: string }) => {
    const { data, error } = await request<any>('POST', '/api/auth/invite/accept', { token, password })
    if (data && !error) {
      setTokens(data.access_token, data.refresh_token)
      const payload = decodeJwt(data.access_token)
      const user = { id: data.user.id, email: data.user.email, ...payload }
      const session = { access_token: data.access_token, user }
      return { data: { user, session }, error: null }
    }
    return { data: { user: null, session: null }, error }
  },

  signOut: async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    await request('POST', '/api/auth/logout', { refresh_token: refreshToken })
    clearTokens()
    return { error: null }
  },

  getSession: async () => {
    const token = getToken()
    if (!token) return { data: { session: null }, error: null }
    const payload = decodeJwt(token)
    if (!payload) return { data: { session: null }, error: null }

    // proactively refresh if token expires within 60s
    if (payload.exp && payload.exp * 1000 < Date.now() + 60_000) {
      const refreshed = await attemptRefresh()
      if (!refreshed) {
        clearTokens()
        return { data: { session: null }, error: null }
      }
    }

    const freshToken = getToken()!
    const freshPayload = decodeJwt(freshToken)!
    const user = { id: freshPayload.sub, email: freshPayload.email, ...freshPayload }
    return {
      data: { session: { access_token: freshToken, user } },
      error: null,
    }
  },

  getUser: async () => {
    const { data: { session }, error } = await auth.getSession()
    return { data: { user: session?.user ?? null }, error }
  },

  updateUser: async ({ password, currentPassword }: { password: string; currentPassword?: string }) => {
    // Settings → Change Password sends both. ResetPasswordPage uses the
    // dedicated /api/auth/reset-password endpoint and never calls this.
    // backend validate middleware requires current_password to be a
    // non-empty string. omitting it (legacy behaviour) gives a 400
    const { data, error } = await request('POST', '/api/auth/change-password', {
      new_password: password,
      current_password: currentPassword,
    })
    return { data, error }
  },

  resetPasswordForEmail: async (email: string, opts?: { redirectTo?: string }) => {
    return request('POST', '/api/auth/forgot-password', {
      email,
      redirectTo: opts?.redirectTo,
    })
  },

  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    // same-tab: custom event fired by setTokens/clearTokens
    const customHandler = (e: CustomEvent) => {
      if (e.detail.type === 'SIGNED_OUT') {
        callback('SIGNED_OUT', null)
      } else if (e.detail.type === 'SIGNED_IN') {
        const payload = decodeJwt(e.detail.token)
        if (payload) {
          const user = { id: payload.sub, email: payload.email, ...payload }
          callback('SIGNED_IN', { access_token: e.detail.token, user })
        }
      }
    }
    // cross-tab: storage event
    const storageHandler = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        if (!e.newValue) {
          callback('SIGNED_OUT', null)
        } else {
          const payload = decodeJwt(e.newValue)
          if (payload) {
            const user = { id: payload.sub, email: payload.email, ...payload }
            callback('SIGNED_IN', { access_token: e.newValue, user })
          }
        }
      }
    }
    window.addEventListener('seacuredb:auth', customHandler as EventListener)
    window.addEventListener('storage', storageHandler)
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            window.removeEventListener('seacuredb:auth', customHandler as EventListener)
            window.removeEventListener('storage', storageHandler)
          },
        },
      },
    }
  },
}

export const storage = {
  from: (bucket: string) => ({
    upload: async (filePath: string, file: Blob | File, _opts?: any) => {
      const fd = new FormData()
      const filename = filePath.split('/').pop() ?? 'file'
      const subdir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : ''
      if (subdir) fd.append('subdir', subdir)
      fd.append('file', file, filename)
      const token = getToken()
      const res = await fetch(`${BASE_URL}/api/files/upload/${bucket}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      })
      const json = await res.json().catch(() => ({}))
      return res.ok ? { data: json, error: null } : { data: null, error: json }
    },

    download: async (filePath: string) => {
      const { data: signData, error } = await request<{ token: string }>(
        'POST', '/api/files/sign', { bucket, path: filePath }
      )
      if (error || !signData) return { data: null, error }
      const token = getToken()
      const res = await fetch(
        `${BASE_URL}/api/files/${bucket}/${filePath}?token=${signData.token}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      )
      if (!res.ok) return { data: null, error: { message: `HTTP ${res.status}` } }
      return { data: await res.blob(), error: null }
    },

    createSignedUrl: async (filePath: string, _expirySeconds: number) => {
      const { data, error } = await request<{ token: string }>(
        'POST', '/api/files/sign', { bucket, path: filePath }
      )
      if (error || !data) return { data: null, error }
      const signedUrl = `${BASE_URL}/api/files/${bucket}/${encodeURIComponent(filePath)}?token=${data.token}`
      return { data: { signedUrl }, error: null }
    },

    getPublicUrl: (filePath: string) => {
      // files require signed URLs, this stub shouldn't be used directly
      return { data: { publicUrl: `${BASE_URL}/api/files/${bucket}/${filePath}` } }
    },

    list: async (prefix?: string, _opts?: any) => {
      const p = prefix ? `?path=${encodeURIComponent(prefix)}` : ''
      return request<any[]>('GET', `/api/files-list/${bucket}${p}`)
    },

    remove: async (paths: string[]) => {
      return request('DELETE', `/api/files/${bucket}`, { paths })
    },
  }),
}

export async function rpc(functionName: string, args: Record<string, any> = {}) {
  return request('POST', `/api/rpc/${functionName}`, args)
}


export const supabase = { from, auth, storage, rpc }
export default supabase
